import { HTMLAttributes, PropsWithChildren, forwardRef, useMemo } from "react";
import { cx } from "@jugl-web/utils";
import { InteractiveContainer } from "../../primitives";
import {
  EmptyListContent,
  EmptyListContentProps,
  LoadingAnimation,
  LoadingAnimationProps,
} from "../../feedback";

export type DataLoadingWrapperProps = HTMLAttributes<HTMLDivElement> &
  PropsWithChildren<{
    isLoading?: boolean;
    onRetry?: () => void;
    isError?: boolean;
    isFetching?: boolean;
    errorMessage?: string;
    animationSize?: LoadingAnimationProps["size"];
    customErrorContentProps?: EmptyListContentProps;
    isLoadingMore?: boolean;
    isLoadingMoreError?: boolean;
    loadingMoreErrorMessage?: string;
    onRetryLoadMore?: () => void;
  }>;

export const DataLoadingWrapper = forwardRef<
  HTMLDivElement,
  DataLoadingWrapperProps
>(
  (
    {
      children,
      isLoading,
      isError,
      isFetching,
      errorMessage,
      onRetry,
      className,
      animationSize,
      customErrorContentProps,
      isLoadingMore,
      isLoadingMoreError,
      loadingMoreErrorMessage,
      onRetryLoadMore,
      ...nativeProps
    },
    ref
  ) => {
    const defaultErrorMessage = "Can't load data";
    const defaultLoadingMoreErrorMessage = "Can't load more data";

    const $componentContent = useMemo(() => {
      if (isLoading && !isLoadingMore) {
        return <LoadingAnimation size={animationSize || "lg"} />;
      }
      if (isError) {
        if (customErrorContentProps) {
          return (
            <div>
              <EmptyListContent
                {...{ onRefetch: onRetry, ...customErrorContentProps }}
              />
            </div>
          );
        }
        return (
          <div className="text-center text-sm text-grey-800">
            {errorMessage || defaultErrorMessage}
            <br />
            {!!onRetry && (
              <InteractiveContainer
                as="span"
                className="font-medium text-primary-500"
                onClick={onRetry}
              >
                Click to retry
              </InteractiveContainer>
            )}
          </div>
        );
      }
      return null;
    }, [
      animationSize,
      customErrorContentProps,
      errorMessage,
      isError,
      isLoading,
      isLoadingMore,
      onRetry,
    ]);

    if (!$componentContent) {
      return (
        <>
          {children}
          {!isError && isLoadingMore && (
            <div className="flex w-full items-center justify-center px-4 py-4">
              <LoadingAnimation size={animationSize || "lg"} />
            </div>
          )}
          {!isError && isLoadingMoreError && (
            <div className="px-4 py-4 text-center text-sm text-grey-800">
              {loadingMoreErrorMessage || defaultLoadingMoreErrorMessage}
              <br />
              {!!onRetryLoadMore && (
                <InteractiveContainer
                  as="span"
                  className="font-medium text-primary-500"
                  onClick={onRetryLoadMore}
                >
                  Click to retry
                </InteractiveContainer>
              )}
            </div>
          )}
        </>
      );
    }

    return (
      <div
        ref={ref}
        {...{
          ...nativeProps,
          className: cx(
            "h-full",
            {
              "flex justify-center items-center px-4 py-8 w-full":
                isLoading || isError,
            },
            {
              relative: isFetching && !isLoading,
            },
            className
          ),
        }}
      >
        {$componentContent || children}
        {isFetching && !isLoading ? (
          <div className="bg-opacity-50 absolute top-0 right-0 bottom-0 left-0 z-[2005] flex animate-fade-in items-center justify-center bg-white backdrop-blur-[0.5px]">
            <LoadingAnimation size={animationSize || "lg"} />
          </div>
        ) : null}
      </div>
    );
  }
);
